import { useRef, useEffect, useCallback, useState } from "react";
import { Flame } from "lucide-react";
import { SplitReveal, FadeUp } from "../components/SplitReveal";
import { useLanguage } from "../lib/i18n";
import { IMAGES } from "../lib/images";

// SELF-HEALING — draw scratches on canvas; they auto-heal (fade) after 1.5s. "Isıt" clears instantly.
export function SelfHealing() {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const healTimer = useRef(null);
  const fadeRAF = useRef(null);
  const fadeSpeed = useRef(0.04);
  const [wave, setWave] = useState(false);

  const resize = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width;
    cv.height = rect.height;
  }, []);

  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      cancelAnimationFrame(fadeRAF.current);
      clearTimeout(healTimer.current);
    };
  }, [resize]);

  const startFade = useCallback(() => {
    cancelAnimationFrame(fadeRAF.current);
    const cv = canvasRef.current;
    const ctx = cv?.getContext("2d");
    if (!ctx) return;
    const loop = () => {
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = `rgba(0,0,0,${fadeSpeed.current})`;
      ctx.fillRect(0, 0, cv.width, cv.height);
      ctx.globalCompositeOperation = "source-over";
      fadeRAF.current = requestAnimationFrame(loop);
    };
    loop();
  }, []);

  const scheduleHeal = useCallback(() => {
    clearTimeout(healTimer.current);
    fadeSpeed.current = 0.04;
    healTimer.current = setTimeout(startFade, 1500);
  }, [startFade]);

  const point = (e) => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const src = e.touches?.[0] || e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const drawTo = (p) => {
    const ctx = canvasRef.current.getContext("2d");
    if (!last.current) {
      last.current = p;
      return;
    }
    ctx.strokeStyle = "rgba(220,225,235,0.9)";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.shadowColor = "rgba(255,255,255,0.6)";
    ctx.shadowBlur = 4;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const onDown = (e) => {
    drawing.current = true;
    cancelAnimationFrame(fadeRAF.current);
    clearTimeout(healTimer.current);
    last.current = point(e);
  };
  const onMove = (e) => {
    if (!drawing.current) return;
    drawTo(point(e));
  };
  const onUp = () => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    scheduleHeal();
  };

  const applyHeat = () => {
    setWave(true);
    setTimeout(() => setWave(false), 900);
    clearTimeout(healTimer.current);
    fadeSpeed.current = 0.22;
    startFade();
  };

  return (
    <section id="tech" className="relative bg-[#0F1115] px-6 py-24 md:px-12 md:py-40" data-testid="healing-section">
      <div className="mx-auto grid max-w-[1600px] items-center gap-12 lg:grid-cols-12">
        <div className="order-2 lg:order-1 lg:col-span-7">
          <div className="relative aspect-[16/10] w-full overflow-hidden rounded-2xl border border-white/5">
            <img src={IMAGES.panelSurface} alt="Koyu araç panel yüzeyi" className="absolute inset-0 h-full w-full object-cover" />
            <canvas
              ref={canvasRef}
              data-cursor
              data-cursor-label="ÇİZ"
              data-testid="healing-canvas"
              className="absolute inset-0 h-full w-full touch-none"
              onMouseDown={onDown}
              onMouseMove={onMove}
              onMouseUp={onUp}
              onMouseLeave={onUp}
              onTouchStart={onDown}
              onTouchMove={onMove}
              onTouchEnd={onUp}
            />
            {/* Heat wave overlay */}
            <div
              className="pointer-events-none absolute inset-0 transition-opacity duration-300"
              style={{
                opacity: wave ? 1 : 0,
                background:
                  "linear-gradient(90deg, transparent, rgba(240,116,42,0.35), transparent)",
                transform: wave ? "translateX(60%)" : "translateX(-60%)",
                transitionProperty: "transform, opacity",
                transitionDuration: "900ms",
                transitionTimingFunction: "ease-in-out",
              }}
              aria-hidden="true"
            />
            <div className="pointer-events-none absolute bottom-4 left-4 font-inter text-xs text-white/50">
              {t.healing.scratch}
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2 lg:col-span-5">
          <FadeUp>
            <p className="heading mb-4 text-sm not-italic tracking-[0.3em] text-hars-orange">{t.healing.kicker}</p>
          </FadeUp>
          <SplitReveal text={t.healing.title} className="heading text-5xl text-white md:text-6xl" />
          <FadeUp delay={0.1}>
            <p className="mt-6 font-inter text-base text-hars-muted md:text-lg">{t.healing.text}</p>
            <button
              onClick={applyHeat}
              data-cursor
              data-cursor-label="ISI"
              data-testid="healing-heat-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-hars-orange px-6 py-3 font-inter text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
            >
              <Flame size={18} /> {t.healing.heat}
            </button>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
